import isString from 'lodash/isString';
import partial from 'lodash/partial';
import { DropdownGroup, DropdownItem, DropdownOption } from '../../types';

/**
 * Adds keyboard support and select functionality to a StandardMenu item
 */
export function itemWithOnSelect(
  item: DropdownOption,
  onSelect?: (label: any, value: any) => void
): DropdownOption {
  return {
    ...item,
    onClick: e => {
      // I would use optional chaining here, but `npm run test` doesn't like it
      if (item.onClick) {
        item.onClick(e);
      }
      if (onSelect) {
        onSelect(item.value, item.label);
      }
    },
  };
}

/** For use with `mapDropdownItemsToIndexes` */
function buildIndexForDropdownGroupItem(
  index: number,
  previousGroupValue,
  groupItem,
  groupInnerIndex: number
): {
  [key: string]: {
    index: number;
    innerIndex: number | null;
    selected: boolean;
  };
} {
  return groupItem.label && isString(groupItem.label)
    ? {
        ...previousGroupValue,
        [groupItem.label]: {
          index,
          innerIndex: groupInnerIndex,
          selected: groupItem.selected,
        },
      }
    : previousGroupValue;
}

/** For use with `mapDropdownItemsToIndexes` */
function buildIndexesForDropdownGroup(
  index: number,
  previousValue,
  item: DropdownGroup
) {
  return {
    ...previousValue,
    ...item.items.reduce(partial(buildIndexForDropdownGroupItem, index), {}),
  };
}

/** For use with `mapDropdownItemsToIndexes` */
function buildIndexForDropdownOption(
  index: number,
  previousValue,
  item: DropdownOption
) {
  return {
    ...previousValue,
    [item.label as string]: {
      index,
      innerIndex: null,
      selected: item.selected,
    },
  };
}

/**
 *  Because our StandardMenu is made up of `DropdownItem`s (which can be a `DropdownOption` or a `DropdownGroup`),
 * tracking what index is currently focused is difficult.
 *
 *  This function flattens a `DropdownItem[]` by mapping each item's label to the index that said item exists within
 * the menu's root (returned object's `index` value) and stores a separate index that indicates where it exists within
 * a DropdownGroup (return object's `innerIndex` value)
 *
 * If `innerIndex` is null, the item exists at the menu's root.
 */
export function mapDropdownItemsToIndexes(items: DropdownItem[]): {
  [key: string]: {
    /** Index of an item within the menu's root */
    index: number;
    /** Index of an item within a group */
    innerIndex: number | null;
    selected: boolean;
  };
} {
  return items.reduce((previousValue, item, index) => {
    const isLabelValidString = item.label && isString(item.label);
    const isDropdownItemAGroup = !!(item as DropdownGroup).items;

    if (isLabelValidString || isDropdownItemAGroup) {
      if ((item as DropdownGroup).items) {
        return buildIndexesForDropdownGroup(
          index,
          previousValue,
          item as DropdownGroup
        );
      } else if (isString(item.label)) {
        return buildIndexForDropdownOption(
          index,
          previousValue,
          item as DropdownOption
        );
      }
    }
    return previousValue;
  }, {});
}
